// Decisions the security page shares between its sections (contract §6.4, §6.6, §6.7).
import type { QueryClient } from "@tanstack/react-query";
import { getAuthSession, hasStrongAmr, isEnrolmentConfined, isRecoverySession, loadAuthSession, type AuthSessionInfo } from "../../../lib/http/session";
import type { MfaFactorsResponse } from "../../../types/auth/mfa";

export const MFA_FACTORS_QUERY_KEY = ["auth", "mfa", "factors"] as const;

export const totpFactors = (data?: MfaFactorsResponse | null) => (data?.factors ?? []).filter((f) => f.type === "totp");

export const recoveryPhone = (data?: MfaFactorsResponse | null) => (data?.factors ?? []).find((f) => f.type === "sms");

export const strongFactorCount = (data?: MfaFactorsResponse | null): number => totpFactors(data).length + (data?.passkeys?.length ?? 0);

// A staff user's first strong factor needs the emailed code; a recovery session already proved one.
export const needsEnrolmentEmailCode = (info: AuthSessionInfo | null | undefined, strongCount: number): boolean =>
	info?.session?.audience === "staff" && strongCount === 0 && !isRecoverySession(info);

// Enrolling does not upgrade the session; one without otp/hwk would be confined at its next refresh, so it steps up with the new factor now.
export const needsFinishingStepUp = (info: AuthSessionInfo | null | undefined = getAuthSession().info): boolean =>
	Boolean(info && (info.mfa?.required || isEnrolmentConfined(info)) && !hasStrongAmr(info));

// Factors and the session's MFA summary change together; both are re-read after every change.
export const refreshSecurityState = async (queryClient: QueryClient): Promise<void> => {
	await Promise.allSettled([queryClient.invalidateQueries({ queryKey: MFA_FACTORS_QUERY_KEY }), loadAuthSession()]);
};
